import React from 'react';
import {
  faBell,
  faBoxesStacked,
  faChartColumn,
  faFileChartColumn,
  faFolder,
  faImages,
  faMicrochip,
  faPrescriptionBottleMedical,
  faUserGear,
  faUsers,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HomeIcon from '../../../images/icons/HomeIcon';
import HubIcon from '../../../images/icons/HubIcon';
import { UserRoles } from '../../constants';
// import { ReactComponent as CapsuleIcon } from '../../../images/icons/capsule.svg'; // Example to import SVG for future use
import { ReactComponent as FacilityIconActive } from '../../../images/icons/facilities_icon_active.svg';
import { ReactComponent as FacilityIconInActive } from '../../../images/icons/facilities_icon_inactive.svg';
import { ReactComponent as PatientIconActive } from '../../../images/icons/patient_icon_active.svg';
import { ReactComponent as PatientIconInActive } from '../../../images/icons/patient_icon_inactive.svg';
import { dimGrey } from '../../../mainTheme';
export interface MenuItem {
  icon: React.ReactElement;
  selectedIcon?: React.ReactElement;
  text: string;
  to: string;
}

export interface BottomMenuItem extends MenuItem {
  visibleFor: UserRoles[];
  alertCount?: number;
}

// Shared base menu
const sharedMenuItems: MenuItem[] = [
  { icon: <HomeIcon />, text: 'Overview', to: '/' },
  { icon: <HubIcon />, text: 'Hubs', to: '/v2/hubs' },
  {
    icon: <FacilityIconInActive />,
    selectedIcon: <FacilityIconActive />,
    text: 'Facilities',
    to: '/v2/facilities',
  },
  {
    icon: <PatientIconInActive />,
    selectedIcon: <PatientIconActive />,
    text: 'Patients',
    to: '/v2/patients',
  },
  // {
  //   icon: <CapsuleIcon />,
  //   selectedIcon: <CapsuleSelectedIcon />,  //  available in images folder
  //   text: 'Adherence',
  //   to: '/v2/adherence',
  // },
  // {
  //   icon: (
  //     <FontAwesomeIcon icon={faTriangleExclamation} size="lg" color={dimGrey} />
  //   ),
  //   text: 'Issues',
  //   to: '/v2/issues',
  // },
  // {
  //   icon: <FontAwesomeIcon icon={faBadgeCheck} size="lg" color={dimGrey} />,
  //   text: 'Patient survey',
  //   to: '/v2/patient-survey',
  // },
  // {
  //   icon: <SoftwareIcon />,
  //   selectedIcon: <SoftwareSelectedIcon />, // available in images folder
  //   text: 'Software',
  //   to: '/v2/software',
  // },
  // {
  //   icon: <FontAwesomeIcon icon={faHeartPulse} size="lg" color={dimGrey} />,
  //   text: 'Vitals',
  //   to: '/vitals',
  // },
  // {
  //   icon: <CareGiverIcon />, // available in images folder
  //   text: 'Caregivers',
  //   to: '/v2/caregiver',
  // },
  // {
  //   icon: <PharmaciesIcon />, // available in images folder
  //   text: 'Pharmacies',
  //   to: '/v2/pharmacies',
  // },
];

const adminMenuItems: MenuItem[] = [
  // Only include: Overview, Facility, Hubs, Patients in this order for Facility Admin
  { icon: <HomeIcon />, text: 'Overview', to: '/' },
  // Facility (with placeholder route for routing to Location Page)
  {
    icon: <FacilityIconInActive />,
    selectedIcon: <FacilityIconActive />,
    text: 'Facility',
    to: '/v2/facilities/__FACILITY_ID__/location/__LOCATION_ID__',
  },
  { icon: <HubIcon />, text: 'Hubs', to: '/v2/hubs' },
  {
    icon: <PatientIconInActive />,
    selectedIcon: <PatientIconActive />,
    text: 'Patients',
    to: '/v2/patients',
  },
];

// Engineering tool-belt (v2-eng-migration-plan Phase 0). v2 routes where a
// modern page exists; classic routes as fallbacks until their phase lands
// (Devices/Software/Reporting/Ops/Engineers/Pharmacies).
const engineeringMenuItems: MenuItem[] = [
  { icon: <HomeIcon />, text: 'Overview', to: '/' },
  { icon: <HubIcon />, text: 'Hubs', to: '/v2/hubs' },
  {
    icon: <FontAwesomeIcon icon={faMicrochip} size="lg" color={dimGrey} />,
    text: 'Devices',
    to: '/devices',
  },
  {
    icon: <FontAwesomeIcon icon={faBoxesStacked} size="lg" color={dimGrey} />,
    text: 'Software',
    to: '/software',
  },
  {
    icon: <FontAwesomeIcon icon={faChartColumn} size="lg" color={dimGrey} />,
    text: 'Call Stats',
    to: '/v2/call-stats',
  },
  {
    icon: (
      <FontAwesomeIcon icon={faFileChartColumn} size="lg" color={dimGrey} />
    ),
    text: 'Reporting',
    to: '/reporting',
  },
  {
    icon: <FontAwesomeIcon icon={faImages} size="lg" color={dimGrey} />,
    text: 'Operations',
    to: '/ops',
  },
  {
    icon: <FontAwesomeIcon icon={faUsers} size="lg" color={dimGrey} />,
    text: 'Owners',
    to: '/v2/owners',
  },
  {
    icon: <FontAwesomeIcon icon={faUserGear} size="lg" color={dimGrey} />,
    text: 'Engineers',
    to: '/engineers',
  },
  {
    icon: <FacilityIconInActive />,
    selectedIcon: <FacilityIconActive />,
    text: 'Facilities',
    to: '/v2/facilities',
  },
  {
    icon: (
      <FontAwesomeIcon
        icon={faPrescriptionBottleMedical}
        size="lg"
        color={dimGrey}
      />
    ),
    text: 'Pharmacies',
    to: '/pharmacies',
  },
];

// Pharmacy technician (privilege_id === 4) menu — scoped to their Patients view
const pharmacyTechMenuItems: MenuItem[] = [
  { icon: <HomeIcon />, text: 'Overview', to: '/' },
  {
    icon: <PatientIconInActive />,
    selectedIcon: <PatientIconActive />,
    text: 'Patients',
    to: '/v2/pharmacy/patients',
  },
];

export const mainMenuConfig = {
  [UserRoles.FACILITY_ADMIN]: {
    heading: '', // This is used to show heading above menu items, keep it blank for Facility Admin
    topSlice: 8,
    items: adminMenuItems,
  },
  [UserRoles.CUSTOMER_CARE]: {
    heading: 'Users',
    topSlice: 2,
    items: [
      ...sharedMenuItems,
      {
        icon: <FontAwesomeIcon icon={faFolder} size="lg" />,
        text: 'Documents',
        to: '/documents',
      },
    ] as MenuItem[],
  },
  [UserRoles.ENGINEERING]: {
    // Tools above the divider, people/org directory below.
    heading: 'Directory',
    topSlice: 7,
    items: engineeringMenuItems,
  },
  [UserRoles.PHARMACY_TECH]: {
    heading: '',
    topSlice: 8,
    items: pharmacyTechMenuItems,
  },
};

export const bottomMenuConfig: BottomMenuItem = {
  icon: <FontAwesomeIcon icon={faBell} size="lg" />,
  text: 'Alerts',
  to: '/alerts',
  visibleFor: [], // currently not used
  alertCount: 4,
};
